import {EditorSDK, PageRef} from '@wix/platform-editor-sdk';
import {EcomPublicApi} from '../../editor-script/editorScript';
import {AppReflowPageVariantID, AppReflowPremiumFeatureName} from '../constants';
import {getNumOfPagesByTpaPageId} from '../utils/pages';
import {doTransaction} from '../../editor-script/transaction';
import {Logger as WebBiLogger} from '@wix/web-bi-logger/dist/src/types';
import {guid} from '@wix/web-bi-logger/dist/src/lib/utils';
import {sendBi} from './bi';
import {BiStoresReflowPageName, RuleDef} from './types';
import {createRule, openRulesModal} from './rulesModal';
import {addReflowPage} from './pages';
import {reportPremiumQuotaUsage} from './premium';
import {IHttpClient} from '@wix/http-client';
import {AppReflowType, PageMap} from '@wix/wixstores-client-core';

export const addCategoryPageVariant = async ({
  editorSDK,
  httpClient,
  variantId,
  ecomPublicApi,
  pageRefToDuplicate,
  t,
  webBiLogger,
  errorReporter,
  metaSiteId,
}: {
  editorSDK: EditorSDK;
  httpClient: IHttpClient;
  variantId: AppReflowPageVariantID;
  ecomPublicApi: EcomPublicApi;
  pageRefToDuplicate?: PageRef;
  t: (key: string) => string;
  webBiLogger: WebBiLogger;
  errorReporter: (e: any) => any;
  metaSiteId: string;
}) => {
  try {
    const biFlowId = guid();

    await editorSDK.editor.openProgressBar('', {
      title: t('newCategoryPage.loader.main.title'),
      totalSteps: 3,
      currentStep: 0,
      stepTitle: t('newProductPage.loader.stepOne.subtitle'),
    });

    const pageRef = await editorSDK.tpa.getPageRefByTPAPageId('', {
      tpaPageId: PageMap.CATEGORY,
    });

    await editorSDK.editor.updateProgressBar('', {
      currentStep: 1,
      stepTitle: t('newProductPage.loader.stepOne.subtitle'),
    });

    const emptyRule = createRule();

    await doTransaction(
      editorSDK,
      async () => {
        const numOfCategoryPageVariants = await getNumOfPagesByTpaPageId(editorSDK, PageMap.CATEGORY_PAGE_VARIANT);

        const newPage: any = await addReflowPage(
          editorSDK,
          variantId,
          pageRefToDuplicate?.id ?? pageRef.id,
          'Category Page'
        );

        await editorSDK.editor.updateProgressBar('', {
          currentStep: 2,
          stepTitle: t('newProductPage.loader.stepTwo.subtitle'),
        });

        const variantPageRef = {id: newPage.pageId, type: 'DESKTOP'} as PageRef;

        const newPageTitle = `Category Page ${numOfCategoryPageVariants + 1}`;

        await editorSDK.editor.routers.addVariantToPage({
          pageRef,
          variantPageRef,
          rule: emptyRule,
          variantId,
          biFlowId,
        });

        await editorSDK.pages.navigateTo('', {pageRef: newPage});

        await editorSDK.editor.updateProgressBar('', {
          currentStep: 3,
          stepTitle: t('newProductPage.loader.stepThree.subtitle'),
        });

        await editorSDK.pages.data.update('', {
          pageRef: newPage,
          data: {
            tpaPageId: PageMap.CATEGORY_PAGE_VARIANT,
            title: newPageTitle,
          },
        });

        await ecomPublicApi.setStateForStoresPages();

        await editorSDK.editor.closeProgressBar('', {});

        await reportPremiumQuotaUsage(AppReflowPremiumFeatureName.CategoryPage, httpClient, metaSiteId, '1');

        const ruleDef: RuleDef = await openRulesModal(
          editorSDK,
          ecomPublicApi,
          t,
          newPageTitle,
          metaSiteId,
          AppReflowType.CategoryPage
        );

        if (ruleDef?.type) {
          const updatedRule = createRule(ruleDef);
          await (editorSDK.editor.routers as any).updateVariantPageRule({variantPageRef, updatedRule, biFlowId});
        }

        return sendBi({
          biStoresReflowPageName: BiStoresReflowPageName.CategoryPage,
          webBiLogger,
          isEdit: false,
          pageVariantId: variantPageRef.id,
          originalPageId: pageRef.id,
          duplicatedPageId: pageRefToDuplicate?.id,
          pageVariantType: variantId,
          ruleDef,
          flowId: biFlowId,
          msid: metaSiteId,
        });
      },
      errorReporter
    );
  } catch (e) {
    errorReporter(e);
  }
};
